import { AlertTriangle, Star } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { CandidateRoleBadge } from './CandidateRoleBadge'
import { CandidateStatBar } from './CandidateStatBar'
import { IdeologyAlignmentBadge } from '@/components/ideology/IdeologyAlignmentBadge'
import { CandidateFactionBadge } from '@/components/faction/CandidateFactionBadge'
import type { Candidate, IdeologyVector } from '@/types'
import { cn } from '@/lib/utils'

interface CandidateListItemProps {
  candidate: Candidate
  partyIdeology: IdeologyVector
  districtName?: string
  onClick?: () => void
  className?: string
}

function getBestSkill(candidate: Candidate): { name: string; value: number } {
  const skills = [
    { name: 'Charisma', value: candidate.charisma },
    { name: 'Admin', value: candidate.admin_skill },
    { name: 'Debate', value: candidate.debate_skill },
  ]
  return skills.reduce((a, b) => (a.value > b.value ? a : b))
}

export function CandidateListItem({
  candidate,
  partyIdeology,
  districtName,
  onClick,
  className,
}: CandidateListItemProps) {
  const isLowLoyalty = candidate.loyalty < 40
  const bestSkill = getBestSkill(candidate)

  return (
    <Card
      className={cn(
        'transition-all',
        onClick && 'cursor-pointer hover:border-muted-foreground/50',
        isLowLoyalty && 'border-red-500/30 bg-red-500/5',
        className
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        {/* Header row: Name, Role, Age */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isLowLoyalty && (
              <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" />
            )}
            <span className="font-medium truncate">{candidate.name}</span>
            <CandidateFactionBadge candidateId={candidate.id} size="sm" showName={false} />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <CandidateRoleBadge
              role={candidate.role}
              districtName={districtName}
            />
            <span className="text-muted-foreground text-sm">
              Age {candidate.age}
            </span>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mb-3">
          <CandidateStatBar
            label="Loyalty"
            value={candidate.loyalty}
            colorMode="positive"
          />
          <CandidateStatBar
            label="Recognition"
            value={candidate.recognition}
            colorMode="positive"
          />
        </div>

        {/* Footer: Best skill and alignment */}
        <div className="flex items-center justify-between gap-2 pt-2 border-t">
          <div className="flex items-center gap-1.5 text-sm">
            <Star className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-muted-foreground">{bestSkill.name}</span>
            <span className="font-medium">{Math.round(bestSkill.value)}</span>
          </div>
          <IdeologyAlignmentBadge
            candidateIdeology={candidate.ideology_vector}
            partyIdeology={partyIdeology}
          />
        </div>

        {/* Low loyalty warning */}
        {isLowLoyalty && (
          <div className="mt-2 pt-2 border-t border-red-500/30">
            <p className="text-xs text-red-500">
              Low loyalty! This candidate may defect or cause problems.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
